import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { htmlToMultilineText } from '#/shared/lib/html'
import { sanitizeSmsText } from '#/shared/lib/sms-sanitize'
import { toast } from '#/shared/ui/sonner'

/** Maximum number of files that can be staged on a single message. */
export const MAX_ATTACHMENTS = 10

/** Maximum combined size (MB) of all files staged on a single message. */
export const MAX_ATTACHMENTS_MB = 10
const MAX_ATTACHMENTS_BYTES = MAX_ATTACHMENTS_MB * 1024 * 1024

export interface ComposerAttachment {
  id: string
  file: File
  /** Object URL for image previews; empty string for non-image files. */
  previewUrl: string
  /** Uppercase file extension shown on the tile badge, e.g. "PNG". */
  ext: string
  isImage: boolean
}

function extOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot + 1).toUpperCase() : ''
}

/** Build a staged attachment, creating a preview URL for image files. */
function makeAttachment(file: File): ComposerAttachment {
  const isImage = file.type.startsWith('image/')
  const previewUrl = isImage ? URL.createObjectURL(file) : ''
  return {
    id: crypto.randomUUID(),
    file,
    previewUrl,
    ext: extOf(file.name),
    isImage,
  }
}

/**
 * Local state for the conversation message composer: the draft plus any staged
 * file attachments. The draft (`text`) is rich-editor HTML; `plainText` is the
 * flattened **and sanitized** body an SMS send actually carries —
 * paragraph/`<br>` boundaries become newlines, and fancy Unicode / invisible
 * characters are folded to plain text (Telnyx spam-blocks them), so the
 * counter, the 1600-char cap, and the optimistic bubble all reflect the real
 * wire body. Voice dictation lives inside the editor now (in-place at the
 * cursor), so this hook no longer owns recording state.
 */
export function useComposer() {
  const { t } = useTranslation()
  const [text, setText] = useState('')
  const plainText = useMemo(
    () => sanitizeSmsText(htmlToMultilineText(text)),
    [text],
  )
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([])
  const urlsRef = useRef<string[]>([])

  // Revoke any outstanding object URLs when the composer unmounts.
  useEffect(
    () => () => {
      for (const url of urlsRef.current) URL.revokeObjectURL(url)
    },
    [],
  )

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return
      // Enforce both caps together: at most MAX_ATTACHMENTS files whose sizes
      // sum to under MAX_ATTACHMENTS_BYTES, counting what's already staged.
      let runningBytes = attachments.reduce((sum, a) => sum + a.file.size, 0)
      const staged: ComposerAttachment[] = []
      let overCount = false
      let overSize = false
      for (const file of Array.from(files)) {
        if (attachments.length + staged.length >= MAX_ATTACHMENTS) {
          overCount = true
          continue
        }
        if (runningBytes + file.size > MAX_ATTACHMENTS_BYTES) {
          overSize = true
          continue
        }
        runningBytes += file.size
        const attachment = makeAttachment(file)
        if (attachment.previewUrl) urlsRef.current.push(attachment.previewUrl)
        staged.push(attachment)
      }
      if (overCount) {
        toast.error(
          t('phonePanel.conversation.maxFiles', { max: MAX_ATTACHMENTS }),
        )
      }
      if (overSize) {
        toast.error(
          t('phonePanel.conversation.maxSize', { max: MAX_ATTACHMENTS_MB }),
        )
      }
      if (staged.length > 0) setAttachments((prev) => [...prev, ...staged])
    },
    [attachments, t],
  )

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id)
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl)
        urlsRef.current = urlsRef.current.filter((u) => u !== target.previewUrl)
      }
      return prev.filter((a) => a.id !== id)
    })
  }, [])

  const reset = useCallback(() => {
    for (const url of urlsRef.current) URL.revokeObjectURL(url)
    urlsRef.current = []
    setText('')
    setAttachments([])
  }, [])

  const hasContent = plainText.trim().length > 0 || attachments.length > 0

  return {
    text,
    setText,
    plainText,
    attachments,
    addFiles,
    removeAttachment,
    reset,
    hasContent,
  }
}

export type ComposerState = ReturnType<typeof useComposer>
